import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { FormCard } from "@/components/FormCard";
import { cn } from "@/lib/utils";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { PartnerFeatureEnum } from "@/__generated__/types";

type EventType = "live" | "game";

export const ChooseEvent = () => {
  const { t } = useTranslation();
  const { navToRoute } = useProtectedNavigation();
  const [selectedEventType, setSelectedEventType] = useState<EventType | null>(
    null,
  );

  const { hasOrgFeature } = useOrgPermissions();
  const hasGameAccess = hasOrgFeature(PartnerFeatureEnum.Games) ?? false;

  const handleCardClick = (eventType: EventType) => {
    setSelectedEventType(eventType);
  };

  const handleNext = () => {
    if (!selectedEventType) return;
    if (selectedEventType === "live") {
      navToRoute("editLiveEvent");
    } else {
      navToRoute("editGame");
    }
  };

  const handleCancel = () => {
    navToRoute("communityTools");
  };

  return (
    <FormCard
      title={t("editEvent.newEvent.cardTitle")}
      description={t("editEvent.newEvent.cardDescription")}
      cancelButton={{
        label: t("global.cancel"),
        onClick: handleCancel,
        variant: "outline",
      }}
      nextButton={{
        label: `${t("global.next")} →`,
        onClick: handleNext,
        disabled: !selectedEventType,
      }}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card
          className={cn(
            "relative my-10 ml-6 w-96 cursor-pointer bg-background shadow-sm transition-all hover:shadow-md",
            selectedEventType === "live"
              ? "rounded-md border-2 border-blue-primary"
              : "rounded-lg border border-border hover:border-muted-foreground/30",
          )}
          onClick={() => handleCardClick("live")}
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <CardTitle className="text-lg">
                {t("editEvent.newEvent.optionCard1Title")}
              </CardTitle>
              {selectedEventType === "live" && (
                <div className="flex size-6 items-center justify-center rounded-full bg-blue-primary text-white">
                  <Check className="size-4" />
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <CardDescription className="text-sm leading-relaxed">
              {t("editEvent.newEvent.optionCard1Description")}
            </CardDescription>
          </CardContent>
        </Card>

        <Card
          className={cn(
            "relative my-10 mr-6 w-96 cursor-pointer bg-background shadow-sm transition-all hover:shadow-md",
            selectedEventType === "game"
              ? "rounded-md border-2 border-blue-primary"
              : "rounded-lg border border-border hover:border-muted-foreground/30",
          )}
          onClick={() => handleCardClick("game")}
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <CardTitle className="text-lg">
                {t("editEvent.newEvent.optionCard2Title")}
              </CardTitle>
              <div className="flex items-center gap-2">
                {selectedEventType === "game" && (
                  <div className="flex size-6 items-center justify-center rounded-full bg-blue-primary text-white">
                    <Check className="size-4" />
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <CardDescription className="mb-3 text-sm leading-relaxed">
              {t("editEvent.newEvent.optionCard2Description")}
            </CardDescription>
            {hasGameAccess === false && (
              <div className="flex justify-end">
                <Badge variant="gradient">{t("global.upgrade")}</Badge>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </FormCard>
  );
};
